// constants/index.js
import {
    Home, Package, MapPin, DollarSign, FileText, Settings,
    Truck, Container, Users, Building2, User, Wrench
} from 'lucide-react';

export const THEME = {
    primary: {
        50: '#eff6ff',
        100: '#dbeafe',
        200: '#bfdbfe',
        300: '#93c5fd',
        400: '#60a5fa',
        500: '#3b82f6',
        600: '#2563eb',
        700: '#1d4ed8',
        800: '#1e40af',
        900: '#1e3a8a'
    },
    success: {
        50: '#f0fdf4',
        100: '#dcfce7',
        500: '#22c55e',
        600: '#16a34a',
        700: '#15803d'
    },
    warning: {
        50: '#fffbeb',
        100: '#fef3c7',
        500: '#f59e0b',
        600: '#d97706',
        700: '#b45309'
    },
    danger: {
        50: '#fef2f2',
        100: '#fee2e2',
        500: '#ef4444',
        600: '#dc2626',
        700: '#b91c1c'
    }
};

export const NAVIGATION_ITEMS = [
    {
        id: 'dashboard',
        name: 'Dashboard',
        icon: Home,
        path: '/dashboard',
        description: 'Overview and analytics'
    },
    {
        id: 'jobs',
        name: 'Jobs',
        icon: Package,
        path: '/jobs',
        description: 'Job management and tracking',
        // subItems: [
        //     { id: 'all-jobs', name: 'All Jobs', path: '/jobs' },
        //     { id: 'create-job', name: 'Create Job', path: '/jobs/create' },
        //     { id: 'import-jobs', name: 'Import Jobs', path: '/jobs?type=import' },
        //     { id: 'export-jobs', name: 'Export Jobs', path: '/jobs?type=export' }
        // ]
    },
    {
        id: 'tracking',
        name: 'Tracking',
        icon: MapPin,
        path: '/tracking',
        description: 'Real-time vehicle tracking'
    },
    {
        id: 'reports',
        name: 'Reports',
        icon: FileText,
        path: '/reports',
        description: 'Analytics and reporting'
    },
    {
        id: 'masters',
        name: 'Masters',
        icon: Settings,
        path: '/masters',
        description: 'Master data management',
        subItems: [
            { id: 'warehouses', name: 'Warehouses', path: '/masters/warehouses', icon: Building2 },
            { id: 'customers', name: 'Customers', path: '/masters/customers', icon: Users },
            { id: 'vendors', name: 'Vendors', path: '/masters/vendors', icon: User },
            { id: 'vehicles', name: 'Vehicles', path: '/masters/vehicles', icon: Truck },
            { id: 'containers', name: 'Containers', path: '/masters/containers', icon: Container },
            { id: 'settings', name: 'Settings', path: '/masters/settings', icon: Wrench }
        ]
    }
];

export const JOB_STATUSES = {
    CREATED: {
        value: 'created',
        label: 'Created',
        color: 'gray',
        bgColor: 'bg-gray-100',
        textColor: 'text-gray-800'
    },
    ASSIGNED: {
        value: 'assigned',
        label: 'Assigned',
        color: 'blue',
        bgColor: 'bg-blue-100',
        textColor: 'text-blue-800'
    },
    IN_TRANSIT: {
        value: 'in_transit',
        label: 'In Transit',
        color: 'yellow',
        bgColor: 'bg-yellow-100',
        textColor: 'text-yellow-800'
    },
    LOADING: {
        value: 'loading',
        label: 'Loading',
        color: 'orange',
        bgColor: 'bg-orange-100',
        textColor: 'text-orange-800'
    },
    UNLOADING: {
        value: 'unloading',
        label: 'Unloading',
        color: 'purple',
        bgColor: 'bg-purple-100',
        textColor: 'text-purple-800'
    },
    COMPLETED: {
        value: 'completed',
        label: 'Completed',
        color: 'green',
        bgColor: 'bg-green-100',
        textColor: 'text-green-800'
    },
    DELAYED: {
        value: 'delayed',
        label: 'Delayed',
        color: 'red',
        bgColor: 'bg-red-100',
        textColor: 'text-red-800'
    },
    CANCELLED: {
        value: 'cancelled',
        label: 'Cancelled',
        color: 'gray',
        bgColor: 'bg-gray-100',
        textColor: 'text-gray-800'
    }
};

export const JOB_TYPES = {
    IMPORT: { value: 'import', label: 'Import', description: 'Port to Warehouse' },
    EXPORT: { value: 'export', label: 'Export', description: 'Warehouse to Port' },
    DOMESTIC: { value: 'domestic', label: 'Domestic', description: 'Warehouse to Customer' },
    VENDOR: { value: 'vendor', label: 'Vendor', description: 'Vendor to Warehouse' }
};

export const CONTAINER_TYPES = {
    '20FT': { value: '20ft', label: '20 FT', size: '20', description: '20 Foot Container' },
    '40FT': { value: '40ft', label: '40 FT', size: '40', description: '40 Foot Container' },
    '40HQ': { value: '40hq', label: '40 HQ', size: '40', description: '40 Foot High Cube' },
    '45FT': { value: '45ft', label: '45 FT', size: '45', description: '45 Foot Container' }
};

export const VEHICLE_TYPES = {
    TRUCK: { value: 'truck', label: 'Truck', capacity: '25T' },
    TRAILER: { value: 'trailer', label: 'Trailer', capacity: '40T' },
    MULTI_AXLE: { value: 'multi_axle', label: 'Multi Axle', capacity: '50T' }
};

export const ALERT_TYPES = {
    INFO: { value: 'info', color: 'blue', icon: 'ℹ️' },
    SUCCESS: { value: 'success', color: 'green', icon: '✅' },
    WARNING: { value: 'warning', color: 'yellow', icon: '⚠️' },
    ERROR: { value: 'error', color: 'red', icon: '❌' },
    DELAY: { value: 'delay', color: 'orange', icon: '⏰' }
};

export const INDIAN_STATES = [
    'Andhra Pradesh', 'Arunachal Pradesh', 'Assam', 'Bihar', 'Chhattisgarh',
    'Goa', 'Gujarat', 'Haryana', 'Himachal Pradesh', 'Jharkhand', 'Karnataka',
    'Kerala', 'Madhya Pradesh', 'Maharashtra', 'Manipur', 'Meghalaya', 'Mizoram',
    'Nagaland', 'Odisha', 'Punjab', 'Rajasthan', 'Sikkim', 'Tamil Nadu',
    'Telangana', 'Tripura', 'Uttar Pradesh', 'Uttarakhand', 'West Bengal'
];

export const MAJOR_PORTS = [
    'JNPT Mumbai', 'Chennai Port', 'Kolkata Port', 'Kandla Port', 'Cochin Port',
    'Paradip Port', 'New Mangalore Port', 'Mormugao Port', 'Tuticorin Port',
    'Ennore Port', 'Kamarajar Port'
];

export const API_ENDPOINTS = {
    JOBS: '/api/jobs',
    TRACKING: '/api/tracking',
    REPORTS: '/api/reports',
    MASTERS: '/api/masters',
    UPLOAD: '/api/upload',
    NOTIFICATIONS: '/api/notifications'
};

export const DATE_FORMATS = {
    DISPLAY: 'DD MMM YYYY',
    INPUT: 'YYYY-MM-DD',
    DATETIME: 'DD MMM YYYY HH:mm',
    TIME: 'HH:mm'
};

export const PAGINATION = {
    DEFAULT_PAGE_SIZE: 20,
    PAGE_SIZE_OPTIONS: [10, 20, 50, 100]
};

export const PERMISSIONS = {
    CREATE_JOB: 'job:create',
    EDIT_JOB: 'job:edit',
    DELETE_JOB: 'job:delete',
    MANAGE_MASTERS: 'masters:manage',
    VIEW_REPORTS: 'reports:view'
};